import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { Spinner } from 'baseui/spinner';
import AuthContext from './shared/AuthContext';
import AuthShell from './auth/AuthShell';
import MfaVerifyStep from './auth/MfaVerifyStep';
import Signup from './SignUp';
import PageMeta from './seo/PageMeta';
import GoogleSignInButton from './auth/GoogleSignInButton';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { formatCommunityStatLine, useCommunityUserStats } from '../hooks/useCommunityUserStats';

const Login = () => {
  const { login, verifyMfaLogin } = useContext(AuthContext);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUpPage, setIsSignup] = useState(false);
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaError, setMfaError] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const { stats: communityStats } = useCommunityUserStats();
  const usernameInputRef = useRef(null);

  useEffect(() => {
    usernameInputRef.current?.focus();
  }, [isSignUpPage, mfaSession]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!formData.username || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    setIsLoading(true);
    setError(null);
    let deviceId = getOrCreateDeviceId();
    try {
      const result = await login({
        email: formData.username,
        password: formData.password,
        deviceId,
      });
      if (!result?.success) {
        setError(result?.error || 'Invalid email or password.');
      } else if (result.mfaRequired) {
        setMfaSession({ mfaToken: result.mfaToken, deviceId });
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (code) => {
    if (!mfaSession) return;
    setMfaLoading(true);
    setMfaError(null);
    const result = await verifyMfaLogin({
      mfaToken: mfaSession.mfaToken,
      code,
      deviceId: mfaSession.deviceId,
    });
    if (!result.success) setMfaError(result.error);
    setMfaLoading(false);
  };

  if (isSignUpPage) {
    return <Signup onBackToLogin={() => setIsSignup(false)} />;
  }

  return (
    <>
      <PageMeta />
      <AuthShell
        title={mfaSession ? 'Verify identity' : 'Welcome back'}
        subtitle={
          mfaSession
            ? 'Enter your authenticator code'
            : formatCommunityStatLine(communityStats) || 'Use your QnaRead account to continue'
        }
        footer={
          !mfaSession && (
            <p className="auth-card-footer">
              New here?{' '}
              <button type="button" className="linkish" onClick={() => setIsSignup(true)}>
                Create an account
              </button>
            </p>
          )
        }
      >
        {error && <div className="auth-error" role="alert">{error}</div>}

        {mfaSession ? (
          <MfaVerifyStep
            onVerify={handleMfaVerify}
            onCancel={() => { setMfaSession(null); setMfaError(null); }}
            loading={mfaLoading}
            error={mfaError}
          />
        ) : (
          <>
            <GoogleSignInButton onError={setGoogleError} />
            {googleError && <div className="auth-error" role="alert">{googleError}</div>}
            <div className="auth-divider" role="separator">
              <span>or continue with email</span>
            </div>
            <form onSubmit={handleLogin}>
            <FormControl label="Email" htmlFor="login-email">
              <Input
                inputRef={usernameInputRef}
                id="login-email"
                name="username"
                type="email"
                autoComplete="email"
                value={formData.username}
                onChange={handleChange}
                placeholder="you@example.com"
              />
            </FormControl>
            <div className="auth-field">
              <FormControl label="Password" htmlFor="login-password">
                <Input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  endEnhancer={() => (
                    <Button
                      type="button"
                      size="mini"
                      kind="tertiary"
                      onClick={() => setShowPassword((p) => !p)}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  )}
                />
              </FormControl>
            </div>
            <Button
              type="submit"
              isLoading={isLoading}
              overrides={{ BaseButton: { style: { width: '100%', marginTop: '8px' } } }}
            >
              {isLoading ? <Spinner size={16} /> : 'Sign in'}
            </Button>
          </form>
          </>
        )}
      </AuthShell>
    </>
  );
};

export default Login;
